import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { useTableState } from '@dotone/react-ui-core/hooks';
import { useCurrentUser, useShallowEqualSelector, useTranslate } from '@hooks';
import { fetchStatList, downloadStats, updateUser } from '@actions';
import { statApprovalConsideredFinal } from '@dotone/react-ui-core/constants';
import { statDefaultColumns, statDefaultColumnsClick, statDataTypes } from '@constants';
import { camelCase } from 'lodash';
import { CardPage, DownloadDropdown, FlexContainer } from '@dotone/react-ui-core';
import { LayoutProtected as Layout } from '@layouts';
import StatTable from './StatTable';
import StatBulkActionDropdown from './StatBulkActionDropdown';
import { BlockInvalidOrderSwitch } from '../widgets';

const StatPage = ({ dataType, filterable = true, ...otherProps }) => {
  const { t } = useTranslate('stat', `pages.${camelCase(dataType)}.index`);
  const [downloading, setDownloading] = useState(false);
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();
  const router = useRouter();
  const { locale, currency, blockInvalidOrders } = currentUser;
  const showInvalidOrderSwitch = dataType !== statDataTypes.CLICKS;

  const [tableProps, setTableProps] = useTableState({
    defaultColumns:
      dataType === statDataTypes.CLICKS ? statDefaultColumnsClick : statDefaultColumns,
    columnType: camelCase(`stat-${dataType}`),
    searches: { dataType },
    dateable: true,
  });
  const { filters, sortField, sortOrder, page, perPage, columns, selected, searchKey } = tableProps;

  const {
    stat: { data, pagination, isLoading, searchParams },
    downloadStatus,
  } = useShallowEqualSelector(({ statDownload, ...state }) => ({
    stat: state[camelCase(`stat-${dataType}-list`)],
    downloadStatus: statDownload.status,
  }));

  const { isLoading: userUpdateLoading } = useShallowEqualSelector(({ userUpdate }) => userUpdate);

  const query = {
    currency,
    locale,
    page,
    perPage,
    sortField,
    sortOrder,
    dataType,
    ...filters,
    searchKey,
  };

  const nonSelectable = data
    .filter((stat) => statApprovalConsideredFinal.network.includes(stat.approval))
    .map((stat) => stat.copyStatId);

  const handleDownload = (downloadFormat) => {
    setDownloading(true);
    dispatch(downloadStats({ ...query, columns, downloadFormat }));
  };

  const handleBlockInvalidOrders = () => {
    dispatch(updateUser(currentUser.id, { blockInvalidOrders: !blockInvalidOrders }, true));
  };

  useEffect(() => {
    setTableProps((prev) => ({
      ...prev,
      filters: { ...prev.filters, blockInvalidOrders },
      page: 1,
    }));
  }, [setTableProps, blockInvalidOrders]);

  useDeepCompareEffect(() => {
    if (currentUser.id) {
      dispatch(fetchStatList(query));
    }
  }, [dispatch, currentUser.id, query]);

  useEffect(() => {
    if (downloading && downloadStatus === 'success') {
      router.push('/advertisers/downloads');
    }
  }, [router, downloading, downloadStatus]);

  useEffect(() => {
    setTableProps((prev) => ({
      ...prev,
      searches: searchParams,
    }));
  }, [setTableProps, searchParams]);

  return (
    <Layout title={t('title')} {...otherProps}>
      <CardPage table basic title={t('title')} subtitle={t('subtitle')}>
        <StatTable
          tableProps={tableProps}
          loading={isLoading}
          data={data}
          pagination={pagination}
          onTableChange={setTableProps}
          dataType={dataType}
          selectRowProps={{ nonSelectable, hideNonSelectable: true }}
          filterable={filterable}
          actions={
            <FlexContainer align="center">
              {showInvalidOrderSwitch && (
                <BlockInvalidOrderSwitch
                  value={currentUser.blockInvalidOrders}
                  onToggle={handleBlockInvalidOrders}
                  className="mr-2"
                  disabled={userUpdateLoading}
                />
              )}
              {dataType !== statDataTypes.CLICKS && (
                <StatBulkActionDropdown ids={selected} className="mr-2" />
              )}
              <DownloadDropdown
                disabled={!data.length || isLoading}
                totalCount={pagination.totalEntries}
                buttonProps={{ submitting: downloading }}
                onClick={handleDownload}
              />
            </FlexContainer>
          }
        />
      </CardPage>
    </Layout>
  );
};

export default StatPage;
